import { isUsDateFormat } from "./tigerTime";

/**
 * Widget-graph x-axis label formatting — port of the C++ `x_label_type`
 * handling (tiger_graph_time.cpp:905-984, parsed from the view ini in
 * single_tiger_view.cpp:4293-4318). The view's GRAPH widget carries the
 * configured value in `func_params`.
 */
export type XLabelType = "time_only" | "date_time" | "date_only" | "none";

const SECS_24_HOURS = 24 * 60 * 60;

/**
 * Resolve the configured `x_label_type` to a concrete format:
 * - `auto` (TIME_GRAPH_DATE_TIME_AUTO_X_LABEL) picks date-only when the
 *   plotted span exceeds 24 hours, else time-only.
 * - an event-scale graph drops the labels entirely (the C++ overrides to
 *   TIME_GRAPH_NO_X_LABEL — the x positions are compressed event windows, so
 *   clock labels would mislead).
 * - anything unset/unknown falls back to the C++ default `time_only`.
 */
export function resolveXLabelType(
  configured: string | null | undefined,
  eventScale: boolean,
  plottedSpanSec: number,
): XLabelType {
  if (eventScale) return "none";
  const t = configured || "time_only";
  if (t === "auto") return plottedSpanSec > SECS_24_HOURS ? "date_only" : "time_only";
  if (t === "date_time" || t === "date_only" || t === "none") return t;
  return "time_only";
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Format one x tick per the resolved label type. Mirrors the C++ formats:
 * `%H:%M:%S`, `%d/%m/%Y` (US sites swap to `%m/%d/%Y`), both for date_time
 * (two lines — uPlot renders `\n` as a line break), blank for none.
 *
 * Tiger timestamps are the site's local wall-clock encoded zone-neutrally
 * (the DAQ stamps with `now_local()`), so decode with the UTC getters like
 * the rest of the UI (tigerTime.ts) — browser-local getters would re-shift
 * the recorded wall time by the viewer's timezone offset.
 */
export function formatXAxisLabel(v: number, type: XLabelType): string {
  if (type === "none") return "";
  const d = new Date(v * 1000);
  const hms = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}`;
  if (type === "time_only") return hms;
  const dd = pad2(d.getUTCDate());
  const mm = pad2(d.getUTCMonth() + 1);
  const date = isUsDateFormat()
    ? `${mm}/${dd}/${d.getUTCFullYear()}`
    : `${dd}/${mm}/${d.getUTCFullYear()}`;
  return type === "date_only" ? date : `${hms}\n${date}`;
}

/**
 * Minimum pixel gap between x ticks for a widget graph drawn at the widget's
 * `10px monospace` (~6px per glyph). uPlot's default is 50px, narrower than
 * the 10-glyph `31/07/2016` date label, so a multi-year graph ticked every
 * half-year drew its dates on top of each other. The date types need room
 * for the date line; time_only for `HH:MM:SS`; none keeps the default.
 */
export function xAxisTickSpacePx(type: XLabelType): number {
  switch (type) {
    case "date_only":
    case "date_time":
      return 90;
    case "time_only":
      return 70;
    default:
      return 50;
  }
}
